"use client";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { DropdownMenu } from "radix-ui";
import { Children, type ComponentProps, type ReactElement, type ReactNode, useMemo } from "react";
import { Button, Typography, cn, useMediaQuery } from "../../../..";

type Props = {
	dropdown:
		| ReactElement<DropdownMenu.DropdownMenuItemProps>
		| ReactElement<DropdownMenu.DropdownMenuItemProps>[];
	children: ReactNode;
	className?: string;
	variant?: "outlined" | "contained";
	color?: ComponentProps<typeof Button>["color"];
	size?: ComponentProps<typeof Button>["size"];
	disabled?: ComponentProps<typeof Button>["disabled"];
};

export function DropDownButton(props: Props) {
	const {
		dropdown,
		children,
		className,
		variant = "contained",
		color = "primary-medium",
		size = "medium",
		disabled,
	} = props;

	const items = useMemo(() => ConvertChildrenToItems(dropdown), [dropdown]);

	return (
		<DropdownMenu.Root>
			<div
				className={cn(className, "flex first:child:rounded-r-none", {
					"first:child:peer first:child:z-1": variant === "outlined",
				})}
			>
				{children}

				{variant === "contained" && <div className="h-full w-0.5 bg-white" />}

				<DropdownMenu.Trigger asChild>
					<Button
						variant={variant}
						color={color}
						size={size}
						disabled={disabled}
						starticon={faChevronDown}
						className={cn("shrink-0 rounded-l-none p-0", {
							"-left-0.5 relative enter:z-2": variant === "outlined",
							"w-[1.75rem]": size === "small",
							"w-[2.5rem]": size === "medium",
							"w-[3.25rem]": size === "large",
						})}
					/>
				</DropdownMenu.Trigger>
			</div>

			<DropdownMenu.Portal>
				<DropdownMenu.Content
					className="scrollbar-none max-h-[var(--radix-dropdown-menu-content-available-height,20rem)] min-w-[220px] max-w-[max(var(--radix-popper-available-width,270px),270px)] snap-y snap-mandatory overflow-auto rounded border border-shades-medium-light bg-white shadow will-change-[opacity,transform] *:snap-start sm:max-w-[calc(min(var(--radix-popper-available-width,840px),840px)/2)]"
					align="end"
					sideOffset={5}
					collisionPadding={5}
				>
					<ItemsWithSeparator>{items}</ItemsWithSeparator>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}

interface DropDownOptionProps extends DropdownMenu.DropdownMenuItemProps {
	icon?: IconProp;
	title: string;
	description: string;
}

export function DropDownOption(props: DropDownOptionProps) {
	const { icon, title, description, children, ...restProps } = props;

	return (
		<DropdownMenu.Item
			{...restProps}
			className="flex cursor-pointer select-none flex-col gap-1 p-4 outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-shades-light data-[disabled]:opacity-50"
		>
			<Typography starticon={icon} variant="paragraph-bold">
				{title}
			</Typography>
			<Typography size="text-sm" color="text-shades-dark">
				{description}
			</Typography>

			{children}
		</DropdownMenu.Item>
	);
}

interface DropDownSubProps extends Omit<DropdownMenu.DropdownMenuSubTriggerProps, "children"> {
	icon?: IconProp;
	title: string;
	description: string;
	children: Props["dropdown"];
}

export function DropDownSub({ icon, title, description, children, ...props }: DropDownSubProps) {
	const isSmallDevice = useMediaQuery({ max: "sm" });
	const items = useMemo(() => ConvertChildrenToItems(children), [children]);

	if (isSmallDevice || Children.count(items) === 1) {
		return <ItemsWithSeparator>{children}</ItemsWithSeparator>;
	}

	return (
		<DropdownMenu.Sub>
			<DropdownMenu.SubTrigger
				{...props}
				className="flex cursor-pointer select-none flex-col gap-1 p-4 outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-shades-light data-[disabled]:opacity-50"
			>
				<Typography starticon={icon} variant="paragraph-bold">
					{title}
				</Typography>
				<Typography size="text-sm" color="text-shades-dark">
					{description}
				</Typography>
			</DropdownMenu.SubTrigger>

			<DropdownMenu.Portal>
				<DropdownMenu.SubContent
					className="scrollbar-none max-h-[var(--radix-dropdown-menu-content-available-height,20rem)] min-w-[220px] max-w-[var(--radix-popper-available-width,270px)] snap-y snap-mandatory overflow-auto rounded border border-shades-medium-light bg-white shadow will-change-[opacity,transform] *:snap-start"
					sideOffset={5}
					collisionPadding={5}
				>
					<ItemsWithSeparator>{items}</ItemsWithSeparator>
				</DropdownMenu.SubContent>
			</DropdownMenu.Portal>
		</DropdownMenu.Sub>
	);
}

function ConvertChildrenToItems(children: Props["dropdown"]): ReactNode {
	if (!Array.isArray(children)) {
		if (Children.count(children.props.children) > 1) {
			return ConvertChildrenToItems(
				children.props.children as ReactElement<DropdownMenu.DropdownMenuItemProps>[],
			);
		}

		return children;
	}

	return Children.map(children, (child) => {
		if (Children.count(child.props.children) > 1) {
			return ConvertChildrenToItems(
				child.props.children as ReactElement<DropdownMenu.DropdownMenuItemProps>[],
			);
		}

		return child;
	});
}

function ItemsWithSeparator({ children }: { children: ReactNode }) {
	return Children.map(children, (child, index) => (
		<>
			{child}

			{index !== Children.count(children) - 1 && (
				<DropdownMenu.Separator className="!snap-align-none h-[1px] bg-shades-medium-light" />
			)}
		</>
	));
}
